import React, { useEffect, useId, useRef, useState } from 'react'
import Button from './Button'
import { InlineAlert, Status } from './ui'

const TYPE_LABELS = { company: 'Компания', contact: 'Контакт' }

// Панель «Добавить контрагента через поиск в CRM» (counterparty-large-accounts).
// Ищет в CRM записи, которых ещё нет в справочнике, и добавляет выбранные.
// Уже добавленные не показывает, а называет числом со ссылкой на поиск по
// справочнику — тот же текст в поле «Найти контрагента в справочнике».
export default function CounterpartyCrmSearch ({ crmName, initialQuery = '', onAdd, onClose, onSearch, onShowInReference }) {
  const titleId = useId()
  const inputId = useId()
  const inputRef = useRef(null)
  const [query, setQuery] = useState(initialQuery)
  const [result, setResult] = useState(null)
  const [searchedQuery, setSearchedQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState(null)
  const [added, setAdded] = useState({})
  const [adding, setAdding] = useState({})
  const activeRef = useRef(true)

  async function search (text = query) {
    const q = text.trim()
    if (!q) return
    setSearching(true)
    setError(null)
    try {
      const data = await onSearch(q)
      if (!activeRef.current) return
      setResult(data)
      setSearchedQuery(q)
    } catch (err) {
      if (activeRef.current) setError(err.message)
    } finally {
      if (activeRef.current) setSearching(false)
    }
  }

  useEffect(() => {
    activeRef.current = true
    const opener = document.activeElement
    inputRef.current?.focus()
    if (initialQuery.trim()) search(initialQuery)
    const onKeyDown = event => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      activeRef.current = false
      document.removeEventListener('keydown', onKeyDown)
      if (opener && typeof opener.focus === 'function') opener.focus()
    }
    // Поиск по тексту, с которым панель открыли, — один раз при открытии.
  }, [])

  async function add (item) {
    const key = `${item.entity_type}:${item.entity_id}`
    setAdding(current => ({ ...current, [key]: true }))
    setError(null)
    try {
      await onAdd(item)
      if (activeRef.current) setAdded(current => ({ ...current, [key]: true }))
    } catch (err) {
      if (activeRef.current) setError(err.message)
    } finally {
      if (activeRef.current) setAdding(current => ({ ...current, [key]: false }))
    }
  }

  const items = result?.items || []
  let status = `${crmName} показывает до 50 совпадений. Если нужного нет, уточните запрос: для контакта — фамилией.`
  if (result) {
    status = items.length
      ? `Найдено в ${crmName} и ещё не в справочнике: ${items.length}.`
      : `В ${crmName} нет новых совпадений. Проверьте написание или введите часть названия.`
    if (result.crm_total && result.crm_total > items.length + (result.in_reference_count || 0)) {
      status += ` Всего совпадений в ${crmName}: ${result.crm_total.toLocaleString('ru-RU')} — уточните запрос.`
    }
  }

  return (
    <div
      className='crm-search__scrim'
      onMouseDown={event => { if (event.target === event.currentTarget) onClose() }}
    >
      <div className='crm-search' role='dialog' aria-modal='true' aria-labelledby={titleId}>
        <div className='crm-search__head'>
          <div>
            <h2 id={titleId}>Добавить контрагента через поиск в {crmName}</h2>
            <p className='crm-search__note'>Ищет компании и контакты {crmName}, которых ещё нет в справочнике. Найденное можно добавить.</p>
          </div>
          <button className='crm-search__close' type='button' aria-label='Закрыть' onClick={onClose}>×</button>
        </div>
        <form
          className='crm-search__form'
          onSubmit={event => {
            event.preventDefault()
            search()
          }}
        >
          <label className='ui-sr-only' htmlFor={inputId}>Название компании или фамилия контакта</label>
          <input
            className='ui-input'
            id={inputId}
            ref={inputRef}
            type='search'
            autoComplete='off'
            placeholder='Название компании или фамилия контакта'
            value={query}
            onChange={event => setQuery(event.target.value)}
          />
          <Button type='submit' loading={searching} disabled={!query.trim()}>Найти</Button>
        </form>
        <p className='crm-search__note' role='status'>{status}</p>
        {error && <InlineAlert title='Не удалось выполнить поиск' tone='error'>{error}</InlineAlert>}
        <div className='crm-search__results'>
          {items.map(item => {
            const key = `${item.entity_type}:${item.entity_id}`
            const name = item.name || 'Название отсутствует'
            return (
              <div className='crm-search__result' key={key}>
                <div className='crm-search__source'>
                  <strong>{name}</strong>
                  <span>{TYPE_LABELS[item.entity_type] || item.entity_type} · id {item.entity_id}</span>
                </div>
                {added[key]
                  ? <Status tone='success'>Добавлено</Status>
                  : (
                    <Button variant='stroke' loading={Boolean(adding[key])} aria-label={`Добавить ${name}`} onClick={() => add(item)}>
                      Добавить
                    </Button>
                    )}
              </div>
            )
          })}
          {result?.in_reference_count > 0 && (
            <div className='crm-search__result'>
              <span className='crm-search__note'>Ещё {result.in_reference_count} уже в справочнике.</span>
              <Button variant='ghost' onClick={() => onShowInReference(searchedQuery)}>Показать в справочнике</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
